import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { usePagination } from "@/hooks/usePagination";
import { throwIfNotOk } from "@/lib/api-error";
import { Pagination } from "@/components/ui/pagination";
import { formatMoney } from "@/lib/admin-payment-breakdown";
import { cn } from "@/lib/utils";
import {
  Check,
  CalendarDots,
  CaretDown,
  CircleNotch,
} from "@phosphor-icons/react";

import {
  toLocalISODate,
  formatDateShort,
  sessionWord,
} from "./payout-helpers";
import {
  PayoutFiltersCard,
  buildPayoutParams,
  emptyPayoutFilters,
  hasAnyPayoutFilter,
  type PayoutFilters,
} from "./PayoutFiltersCard";
import { EmptyState } from "./EmptyState";
import { HistorySkeleton } from "./Skeletons";
import { PayoutDetailRow, PayoutTotals, payoutAmountTooltip } from "./PayoutDetailRow";
import { PayoutAvatar } from "./primitives";

import type { PaginatedPayouts, PayoutDetail } from "@/types";

const ITEMS_PER_PAGE = 20;

export function PayoutHistoryTab() {
  const pagination = usePagination<PayoutFilters>(
    { pageSize: ITEMS_PER_PAGE },
    emptyPayoutFilters,
  );
  const page = pagination.page;
  const setPage = pagination.setPage;
  const filters = pagination.filters;
  const [expandedPayoutId, setExpandedPayoutId] = useState<string | null>(null);

  const historyQuery = useQuery({
    queryKey: [
      "admin",
      "payouts",
      "history",
      page,
      filters.therapistId ?? "",
      filters.dateFrom ? toLocalISODate(filters.dateFrom) : "",
      filters.dateTo ? toLocalISODate(filters.dateTo) : "",
    ],
    queryFn: async () => {
      const params = buildPayoutParams(page, ITEMS_PER_PAGE, filters);
      const res = await fetch(`/api/admin/payouts?${params}`);
      await throwIfNotOk(res, "Greška pri učitavanju historije isplata");
      return res.json() as Promise<PaginatedPayouts>;
    },
    placeholderData: (prev) => prev,
  });

  const payoutDetailQuery = useQuery({
    queryKey: ["admin", "payouts", expandedPayoutId],
    queryFn: async () => {
      const res = await fetch(`/api/admin/payouts/${expandedPayoutId}`);
      await throwIfNotOk(res, "Greška pri učitavanju detalja isplate");
      return res.json() as Promise<PayoutDetail>;
    },
    enabled: !!expandedPayoutId,
  });

  const payouts = historyQuery.data?.data ?? [];
  const totalPages = historyQuery.data?.totalPages ?? 1;

  const hasFilters = hasAnyPayoutFilter(filters);
  const clearFilters = pagination.resetAll;

  return (
    <div className="space-y-4 sm:space-y-5">
      <PayoutFiltersCard
        filters={filters}
        onFilterChange={pagination.setFilter}
        onClear={clearFilters}
      />

      {/* Table */}
      {historyQuery.isLoading && payouts.length === 0 ? (
        <HistorySkeleton />
      ) : payouts.length === 0 ? (
        <EmptyState
          icon={Check}
          title="Nema historije isplata"
          description={hasFilters ? "Pokušajte sa drugim filterima." : undefined}
          hasFilters={hasFilters}
          onClear={clearFilters}
        />
      ) : (
        <>
          {/* Desktop */}
          <div className="hidden lg:block">
            <div className="overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm">
              <table className="w-full table-fixed text-[13px]">
                <colgroup>
                  <col className="w-[36px]" />
                  <col className="w-[110px]" />
                  <col />
                  <col className="w-[100px]" />
                  <col className="w-[60px]" />
                  <col />
                  <col />
                </colgroup>
                <thead>
                  <tr className="bg-muted/40">
                    <th className="px-3 py-2.5" />
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                      Datum
                    </th>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                      Terapeut
                    </th>
                    <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                      Isplaćeno
                    </th>
                    <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                      Sesije
                    </th>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                      Admin
                    </th>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                      Napomena
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((payout, i) => {
                    const isExpanded = expandedPayoutId === payout.id;
                    return (
                      <PayoutDetailRow
                        key={payout.id}
                        payout={payout}
                        isLast={i === payouts.length - 1}
                        isExpanded={isExpanded}
                        onToggle={() =>
                          setExpandedPayoutId(isExpanded ? null : payout.id)
                        }
                        detail={isExpanded ? payoutDetailQuery.data : undefined}
                        detailLoading={isExpanded && payoutDetailQuery.isLoading}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile/Tablet */}
          <div className="grid gap-3 sm:grid-cols-2 lg:hidden">
            {payouts.map((payout) => {
              const isExpanded = expandedPayoutId === payout.id;
              return (
                <div
                  key={payout.id}
                  className={cn(
                    "rounded-2xl border border-border/60 bg-card shadow-sm transition-shadow hover:shadow-md",
                    isExpanded && "sm:col-span-2",
                  )}
                >
                  <button
                    onClick={() =>
                      setExpandedPayoutId(isExpanded ? null : payout.id)
                    }
                    className="flex w-full items-start justify-between gap-2 p-4 text-left"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <PayoutAvatar
                          name={payout.therapistName}
                          tone="primary"
                          className="size-6 text-[9px]"
                        />
                        <span className="truncate text-sm font-medium text-foreground">
                          {payout.therapistName}
                        </span>
                      </div>
                      <div className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                        <CalendarDots className="size-3 shrink-0" />
                        {formatDateShort(payout.createdAt)}
                        <span>·</span>
                        <span>
                          {payout.sessionCount} {sessionWord(payout.sessionCount)}
                        </span>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span
                        className="text-sm font-semibold tabular-nums text-foreground"
                        title={payoutAmountTooltip(payout)}
                      >
                        {formatMoney(payout.amount)}
                        {payout.amount != null && (
                          <span className="ml-0.5 text-[11px] font-normal text-muted-foreground">
                            KM
                          </span>
                        )}
                      </span>
                      <CaretDown
                        className={cn(
                          "size-4 text-muted-foreground transition-transform duration-200",
                          isExpanded && "rotate-180 text-primary",
                        )}
                      />
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="border-t border-border/40 px-4 py-3">
                      {payoutDetailQuery.isLoading ? (
                        <div className="flex items-center justify-center py-4">
                          <CircleNotch className="size-5 animate-spin text-primary" />
                        </div>
                      ) : payoutDetailQuery.data?.sessions ? (
                        <div className="space-y-2">
                          {payout.note && (
                            <p className="rounded-lg bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                              {payout.note}
                            </p>
                          )}
                          {payoutDetailQuery.data.sessions.map((s) => (
                            <div
                              key={s.id}
                              className="flex items-center justify-between text-xs"
                            >
                              <div className="min-w-0">
                                <span className="font-medium text-foreground">
                                  {s.clientName}
                                </span>
                                <span className="mx-1 text-muted-foreground">·</span>
                                <span className="text-muted-foreground">
                                  {formatDateShort(s.scheduledAt)}
                                </span>
                              </div>
                              <span className="shrink-0 font-medium tabular-nums text-foreground">
                                {formatMoney(s.therapistEarnings)}
                                {s.therapistEarnings != null && (
                                  <span className="ml-0.5 text-[10px] font-normal text-muted-foreground">
                                    KM
                                  </span>
                                )}
                              </span>
                            </div>
                          ))}
                          <div className="border-t border-border/40 pt-2">
                            <PayoutTotals payout={payoutDetailQuery.data} />
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}
